import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import BackgroundVideo from "@/components/BackgroundVideo";
import KineticText from "@/components/KineticText";
import Marquee from "@/components/Marquee";
import SiteFooter from "@/components/SiteFooter";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

const Chapter = ({ children, testId }) => (
  <motion.div
    data-testid={testId}
    variants={fadeUp}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-80px" }}
    className="py-10 md:py-14"
  >
    {children}
  </motion.div>
);

const Manifesto = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <motion.div
      data-testid="manifesto-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, y: -30 }}
      transition={{ duration: 0.5 }}
      className="relative"
    >
      <BackgroundVideo />
      <div className="grain-overlay" />

      <header className="relative z-10 flex justify-center pt-10 md:pt-14">
        <motion.img
          src="/assets/oneskin-logo.png"
          alt="One Skin"
          data-testid="site-logo"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="w-36 md:w-48"
        />
      </header>

      <section
        ref={heroRef}
        className="relative z-10 min-h-[82vh] flex items-center px-8 md:px-16 lg:px-24"
      >
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="max-w-5xl lg:ml-[38%]">
          <KineticText
            testIdPrefix="hero-line"
            lines={[
              {
                text: "Functional skincare.",
                className:
                  "font-heading font-black text-[#1E3A2F] text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight",
              },
              {
                text: "Built skintifically.",
                className:
                  "font-heading font-black italic text-[#8B1A2A] text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight",
              },
              {
                text: "Lived unapologetically.",
                className:
                  "font-heading font-black text-[#1E3A2F] text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight",
              },
            ]}
          />
        </motion.div>
      </section>

      <Marquee />

      <section className="relative z-10 px-8 md:px-16 lg:px-24 py-16 md:py-24">
        <div className="max-w-3xl lg:ml-[38%]">
          <Chapter testId="chapter-01">
            <h2 className="font-heading font-bold text-[#1E3A2F] text-2xl md:text-4xl mb-6">
              Skincare for every day.
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#3D6B55] font-medium">
              For the days your skin isn&#8217;t behaving.
              <br />
              For the days progress feels slow.
              <br />
              And for the days your skin finally feels good.
            </p>
          </Chapter>

          <Chapter testId="chapter-02">
            <p className="text-base md:text-lg leading-relaxed text-[#3D6B55] font-medium mb-6">
              Because better skin isn&#8217;t the finish line.
            </p>
            <h2 className="font-heading font-bold text-[#1E3A2F] text-2xl md:text-4xl">
              Your skin deserves care, <span className="italic text-[#B5404A]">every day.</span>
            </h2>
          </Chapter>

          <Chapter testId="chapter-03">
            <h2 className="font-heading font-black text-[#8B1A2A] text-3xl md:text-5xl mb-8">
              Launching soon.
            </h2>
            <div className="flex flex-col items-start gap-4">
              <Link to="/early-access">
                <motion.button
                  data-testid="be-first-in-line-btn"
                  whileHover={{ y: -4 }}
                  whileTap={{ scale: 0.97 }}
                  className="group inline-flex items-center gap-3 bg-[#1E3A2F] text-[#F5EFE6] px-9 py-4 rounded-full font-bold uppercase tracking-[0.15em] text-sm hover:bg-[#3D6B55] transition-colors duration-300 shadow-[0_18px_40px_-16px_rgba(30,58,47,0.5)]"
                >
                  Be first in line
                  <ArrowRight
                    className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5"
                    strokeWidth={2.5}
                  />
                </motion.button>
              </Link>
              <Link to="/about">
                <motion.button
                  data-testid="know-more-btn"
                  whileHover={{ y: -4 }}
                  whileTap={{ scale: 0.97 }}
                  className="group inline-flex items-center gap-3 border-2 border-[#8B1A2A]/40 text-[#8B1A2A] px-9 py-4 rounded-full font-bold uppercase tracking-[0.15em] text-sm hover:bg-[#8B1A2A] hover:text-[#F5EFE6] hover:border-[#8B1A2A] transition-colors duration-300"
                >
                  Know more about us
                  <ArrowRight
                    className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5"
                    strokeWidth={2.5}
                  />
                </motion.button>
              </Link>
            </div>
          </Chapter>
        </div>
      </section>

      <SiteFooter />
    </motion.div>
  );
};

export default Manifesto;
